import { AppointmentStatus } from '@server/prisma-generated/enums';
import { History, Loader2, Terminal } from 'lucide-react';
import { useState } from 'react';
import { trpc } from '~/api/trpc';
import {
    AppointmentHistoryTable,
    countCalledOff,
    countLateCancellations,
    countLateMoves,
    countMovedByKalia,
    formatDateTime,
    HighlightedOnlySwitch,
    HistoryStatCard,
    type HistoryTone,
    isHighlightedEntry,
    LATE_THRESHOLD_HOURS,
} from '~/components/appointments/appointmentHistory';
import { Alert, AlertDescription, AlertTitle } from '~/components/ui/alert';
import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '~/components/ui/dialog';
import { cn } from '~/lib/utils';

const FAILED_STATUSES: AppointmentStatus[] = [
    AppointmentStatus.CANCELLED,
    AppointmentStatus.NO_SHOW,
    AppointmentStatus.DECLINED,
    AppointmentStatus.CALLED_OFF,
];

function HistoryStats({ stats, compact }: { stats: { label: string; count: number; tone: HistoryTone }[]; compact?: boolean }) {
    return (
        <div className={cn('grid grid-cols-2 gap-2', compact ? 'sm:grid-cols-4' : 'md:grid-cols-4 gap-3')}>
            {stats.map((stat) => (
                <HistoryStatCard key={stat.label} label={stat.label} count={stat.count} tone={stat.tone} compact={compact} />
            ))}
        </div>
    );
}

type CustomerAppointmentHistoryProps = {
    customerId: number;
    /** Hides the inline stats and only loads the history once the dialog is opened */
    buttonOnly?: boolean;
};

export function CustomerAppointmentHistory({ customerId, buttonOnly }: CustomerAppointmentHistoryProps) {
    const [open, setOpen] = useState(false);
    const [highlightedOnly, setHighlightedOnly] = useState(false);
    const historyQuery = trpc.appointments.getCustomerAppointmentHistory.useQuery(
        { customerId },
        { enabled: !buttonOnly || open }
    );

    const appointments = historyQuery.data ?? [];
    const allEntries = appointments.flatMap((appointment) => appointment.entries);
    const visibleAppointments = highlightedOnly
        ? appointments
              .map((appointment) => ({ ...appointment, entries: appointment.entries.filter(isHighlightedEntry) }))
              .filter((appointment) => appointment.entries.length > 0)
        : appointments;
    const stats: { label: string; count: number; tone: HistoryTone }[] = [
        {
            label: 'No-Shows',
            count: appointments.filter((appointment) => appointment.status === AppointmentStatus.NO_SHOW).length,
            tone: 'red',
        },
        { label: `Kurzfristig abgesagt durch Kunde (< ${LATE_THRESHOLD_HOURS}h)`, count: countLateCancellations(allEntries), tone: 'red' },
        { label: `Kurzfristig verschoben durch Kunde (< ${LATE_THRESHOLD_HOURS}h)`, count: countLateMoves(allEntries), tone: 'red' },
        { label: 'Durch Kalia verschoben oder abgesagt', count: countCalledOff(allEntries) + countMovedByKalia(allEntries), tone: 'yellow' },
    ];

    if (!buttonOnly && historyQuery.isLoading) {
        return <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />;
    }

    return (
        <div className="flex flex-wrap items-center gap-2">
            {!buttonOnly && <HistoryStats stats={stats} compact />}
            <Button type="button" variant="outline" size="sm" onClick={() => setOpen(true)}>
                <History className="h-4 w-4" />
                Stornierungen und Verschiebungen
            </Button>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Verlauf aller Termine</DialogTitle>
                        <DialogDescription>Verschiebungen, Absagen und No-Shows dieses Patienten</DialogDescription>
                    </DialogHeader>

                    <HistoryStats stats={stats} />

                    <HighlightedOnlySwitch checked={highlightedOnly} onCheckedChange={setHighlightedOnly} />

                    {historyQuery.isError && (
                        <Alert variant="destructive">
                            <Terminal className="h-4 w-4" />
                            <AlertTitle>Fehler beim Laden des Verlaufs</AlertTitle>
                            <AlertDescription>{historyQuery.error.message}</AlertDescription>
                        </Alert>
                    )}

                    {historyQuery.isLoading ? (
                        <div className="flex justify-center py-8">
                            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                        </div>
                    ) : visibleAppointments.length === 0 ? (
                        <p className="py-8 text-center text-sm text-muted-foreground">
                            {highlightedOnly ? 'Keine roten oder gelben Ereignisse vorhanden' : 'Keine Termine vorhanden'}
                        </p>
                    ) : (
                        <div className="space-y-4">
                            {visibleAppointments.map((appointment) => (
                                <section key={appointment.id} className="rounded-lg border p-3">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <span className="font-semibold">
                                            {appointment.startsAt ? formatDateTime(appointment.startsAt) : '–'}
                                        </span>
                                        <span>{appointment.title}</span>
                                        {appointment.storeName && (
                                            <span className="text-sm text-muted-foreground">{appointment.storeName}</span>
                                        )}
                                        <Badge
                                            className="ml-auto"
                                            variant={
                                                appointment.status === AppointmentStatus.COMPLETED
                                                    ? 'default'
                                                    : FAILED_STATUSES.includes(appointment.status)
                                                      ? 'destructive'
                                                      : 'outline'
                                            }
                                        >
                                            {appointment.status}
                                        </Badge>
                                    </div>
                                    {appointment.entries.length === 0 ? (
                                        <p className="mt-2 text-sm text-muted-foreground">Keine Änderungen</p>
                                    ) : (
                                        <AppointmentHistoryTable entries={appointment.entries} />
                                    )}
                                </section>
                            ))}
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
